
import AutocompleteInput from '@/components/forms/AutocompleteInput.vue'
import FormInput2 from '@/components/forms/FormInput2.vue'
import type { LookupFunction } from '@/helpers/AddressHelpers'
import { createAddressLookup } from '@/helpers/AddressHelpers'
import environment from '@/helpers/Environment'
import type { AutocompletedAddressData } from '@/helpers/IdentityHelpers'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { apiErrorMessageOrRethrow, logInTrackJS } from '@/errors/helpers'

@Component({
  name: 'AddressInput',
  components: { AutocompleteInput, FormInput2 },
})
export default class extends Vue {
  @Prop() value: AutocompletedAddressData | null
  @Prop() country: string | null
  @Prop() inputMode: 'autocomplete' | 'manual'
  @Prop() field: FormInputField
  @Prop() required: boolean | null

  errorInternal: string | null = null

  addresses: AutocompletedAddressData[] | null = null
  selectedAddress: AutocompletedAddressData | null = null
  minSearchLength = 4
  timeout: number
  addressLookup: LookupFunction | null = null

  searchInput: string = ''

  created() {
    if (this.country) {
      this.setupAutocompleteClient(this.country)
    }
  }

  get autocompleteEnabled() {
    return this.inputMode === 'autocomplete'
  }

  get input() {
    if (this.autocompleteEnabled) {
      return this.value ? this.formattedItem(this.value) : ''
    } else {
      return this.value?.address ?? ''
    }
  }

  set input(value: string) {
    if (this.autocompleteEnabled) {
      this.$emit('input', { address: value, city: '', state: '', zipCode: '' })
    } else {
      this.$emit('input', { address: value })
    }
  }

  get intermediateInput() {
    if (this.addressLookup && this.autocompleteEnabled) {
      return this.searchInput
    } else {
      return this.input
    }
  }

  set intermediateInput(value: string) {
    if (this.addressLookup && this.autocompleteEnabled) {
      this.searchInput = value
    } else {
      this.input = value
    }
  }

  // Triggered on both new address suggestions and focusing the input.
  @Watch('addresses')
  scrollTo() {
    // TODO Is there a better way of doing this?
    if (this.autocompleteEnabled) {
      this.$nextTick(() => {
        this.$el.scrollIntoView()
      })
    }
  }

  @Watch('country')
  setupAutocompleteClient(country: string) {
    this.selectedAddress = null
    this.addresses = null
    const apiKey = environment.web.address_autocomplete?.api_key
    if (!apiKey) {
      this.addressLookup = null
    } else {
      createAddressLookup(apiKey, country).then((lookup) => {
        this.addressLookup = lookup
      })
    }
  }

  @Watch('input')
  onInputChange(value: string) {
    this.searchInput = value
  }

  @Watch('searchInput')
  onSearchInputChange(value: string) {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }

    if (!this.shouldSearch(value)) {
      this.addresses = null
      return
    }

    this.errorInternal = null
    this.selectedAddress = null

    if (this.country) {
      this.timeout = setTimeout(() => this.lookupAddress(value), 300)
    } else {
      this.addresses = null
    }
  }

  shouldSearch(input: string): boolean {
    return (
      Boolean(this.addressLookup) &&
      this.autocompleteEnabled &&
      input.length >= this.minSearchLength &&
      (!this.selectedAddress || input !== this.formattedItem(this.selectedAddress))
    )
  }

  lookupAddress(input: string) {
    if (!this.country) {
      this.addressLookup = null
    } else {
      this.addressLookup!(input, this.country)
        .then((resp) => {
          this.addresses = resp.result.map(this.formatAddress)
        })
        .catch((error) => {
          // Revert to manual address entry on error.
          this.switchToManualMode()

          if (error?.error?.message) {
            // Smarty error.
            logInTrackJS(error.error.message, error)
          } else {
            apiErrorMessageOrRethrow(error)
          }
        })
    }
  }

  switchToManualMode() {
    this.input = this.searchInput
    this.$emit('manualModeClick')
  }

  @Watch('inputMode')
  onInputModeChange() {
    if (this.value) {
      this.errorInternal = this.error
    }
    this.onSearchInputChange(this.input)
  }

  addressSelected(address: AutocompletedAddressData | null) {
    this.selectedAddress = address
    this.validate()
    if (this.selectedAddress) {
      this.$emit('input', this.selectedAddress)
      this.searchInput = this.formattedItem(this.selectedAddress)
    }
  }

  formatAddress(address: any): AutocompletedAddressData {
    return {
      address: address.streetLine || address.street,
      city: address.city || address.locality,
      state: address.state || address.administrativeArea,
      zipCode: address.zipcode || address.postalCode,
    }
  }

  @Watch('disabled')
  validate() {
    const input = this.inputEl
    this.errorInternal = this.error
    this.$emit('validation', this.error, input)
  }

  get error() {
    if (this.autocompleteEnabled && !this.selectedAddress) {
      return 'Select an address from the list of suggestions or <i>enter manually</i>.'
    } else {
      return null
    }
  }

  get inputEl() {
    return this.$el.querySelector('input, select') as HTMLInputElement
  }

  get eventListeners() {
    return {
      ...this.$listeners,
      // Maintain compatibility with v-model.
      // @see https://vuejs.org/v2/guide/components-custom-events.html#Binding-Native-Events-to-Components
      input: () => null,
    }
  }

  formattedItem(data: AutocompletedAddressData): string {
    if (!data) {
      return ''
    }

    return Object.values(data)
      .filter((line) => line)
      .join(', ')
  }

  get placeholder(): string | undefined {
    if (this.autocompleteEnabled) {
      return 'Enter a location'
    } else if (this.field.showPlaceholderDropdownOption) {
      return this.field.placeholderLanguage
    } else {
      return undefined
    }
  }
}
